import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Button,
  Collapse,
  IconButton,
  Divider,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AccountBalanceWallet as WalletIcon,
  FilterList as FilterListIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreDownIcon,
  Visibility as VisibilityIcon // ✅ Import de l'icône pour le bouton "Voir détails"
} from '@mui/icons-material';
import dayjs from 'dayjs';
import adminFinancialService from '../../services/adminFinancialService';

// ✅ Import du modal de détails de réservation
import ReservationDetailsModal from '../financial/ReservationDetailsModal';

// Constantes pour les filtres
const RESERVATION_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired'
};

const PAYMENT_METHOD = {
  STRIPE: 'stripe',
  CASH: 'cash',
  VIREMENT: 'virement',
  CARD: 'card',
  BANK_TRANSFER: 'bank_transfer',
  ADMIN_ADJUSTMENT: 'admin_adjustment',
  DEPOSIT_CAPTURE: 'deposit_capture',
  STRIPE_REFUND: 'stripe_refund',
  AUTOMATIC_CREDIT: 'automatic_credit'
};

const RESERVATION_STATUS_LABELS = {
  [RESERVATION_STATUS.PENDING]: 'En attente',
  [RESERVATION_STATUS.CONFIRMED]: 'Confirmée',
  [RESERVATION_STATUS.ACTIVE]: 'Active',
  [RESERVATION_STATUS.COMPLETED]: 'Terminée',
  [RESERVATION_STATUS.CANCELLED]: 'Annulée',
  [RESERVATION_STATUS.EXPIRED]: 'Expirée'
};

const PAYMENT_METHOD_LABELS = {
  [PAYMENT_METHOD.STRIPE]: 'Stripe',
  [PAYMENT_METHOD.CASH]: 'Espèces',
  [PAYMENT_METHOD.VIREMENT]: 'Virement',
  [PAYMENT_METHOD.CARD]: 'Carte',
  [PAYMENT_METHOD.BANK_TRANSFER]: 'Virement bancaire',
  [PAYMENT_METHOD.ADMIN_ADJUSTMENT]: 'Ajustement admin',
  [PAYMENT_METHOD.DEPOSIT_CAPTURE]: 'Capture dépôt',
  [PAYMENT_METHOD.STRIPE_REFUND]: 'Remboursement Stripe',
  [PAYMENT_METHOD.AUTOMATIC_CREDIT]: 'Crédit automatique'
};

/**
 * Composant d'administration pour visualiser les paiements groupés par propriétaire
 * Inspiré de PaymentHistoryInfinite mais spécifique à l'interface admin
 */
const AdminPaymentsByOwner = () => {
  // États pour les données - pagination infinie
  const [ownersData, setOwnersData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // États pour les filtres
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    dateStart: '',
    dateEnd: '',
    ownerId: '',
    reservationStatus: '',
    paymentMethod: '',
    ownerEmail: '',
    currency: '', // ✅ Ajout du filtre par devise
    limit: 5 // Limite réduite pour optimiser les performances
  });

  // États pour l'UI
  const [expandedOwner, setExpandedOwner] = useState(null);
  const [expandedReservation, setExpandedReservation] = useState(null);
  const [expandedCurrencyDetails, setExpandedCurrencyDetails] = useState({}); // Pour gérer l'expansion des détails par devise
  const [expandedCurrencyList, setExpandedCurrencyList] = useState({}); // Pour gérer l'expansion de la liste des devises par propriétaire

  // ✅ État pour les devises disponibles
  const [availableCurrencies, setAvailableCurrencies] = useState([]);

  // ✅ États pour le modal de détails de réservation
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /**
   * Charge les données des propriétaires et leurs réservations
   * @param {boolean} isLoadMore - True si on charge plus de données, false pour reset
   */
  const fetchOwnerReservations = async (isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setCurrentPage(1);
    }
    setError(null);

    try {
      const page = isLoadMore ? currentPage + 1 : 1;
      const requestFilters = {
        ...filters,
        page
      };

      console.log('🔄 [AdminPaymentsByOwner] Fetching owner reservations...', requestFilters);

      const result = await adminFinancialService.getReservationsByOwner(requestFilters);

      console.log('📡 [AdminPaymentsByOwner] Backend response:', {
        success: result.success,
        dataPresent: !!result.data,
        ownersCount: result.data?.owners?.length,
        pagination: result.data?.pagination,
        appliedFilters: requestFilters
      });

      if (result.success) {
        const newOwners = result.data.owners || [];
        
        // Debug: vérifier la structure des données reçues
        console.log('🔍 [DEBUG] Données reçues du backend:', {
          isLoadMore,
          page,
          newOwnersCount: newOwners.length,
          newOwners: newOwners.map(ownerData => ({
            id: ownerData.owner.id,
            name: ownerData.owner.name,
            reservationsCount: ownerData.reservations?.length || 0
          }))
        });
        
        if (isLoadMore) {
          // Fusionner intelligemment les propriétaires et leurs réservations
          setOwnersData(prevData => {
            console.log('🔍 [DEBUG] Fusion des données:', {
              existingOwnersCount: prevData.length,
              existingOwners: prevData.map(ownerData => ({
                id: ownerData.owner.id,
                name: ownerData.owner.name,
                reservationsCount: ownerData.reservations?.length || 0
              }))
            });
            
            const mergedOwners = [...prevData];
            
            newOwners.forEach(newOwner => {
              const existingOwnerIndex = mergedOwners.findIndex(owner => owner.owner.id === newOwner.owner.id);
              
              if (existingOwnerIndex >= 0) {
                console.log(`🔄 Fusion pour propriétaire ${newOwner.owner.name} (${newOwner.owner.id})`);
                // Propriétaire existe déjà : fusionner ses réservations
                const existingOwner = mergedOwners[existingOwnerIndex];
                const newReservations = newOwner.reservations || [];
                const existingReservations = existingOwner.reservations || [];
                
                // Éviter les doublons de réservations
                const existingReservationIds = existingReservations.map(r => r.id);
                const uniqueNewReservations = newReservations.filter(r => !existingReservationIds.includes(r.id));
                
                console.log(`📊 Réservations pour ${newOwner.owner.name}: ${existingReservations.length} existantes + ${uniqueNewReservations.length} nouvelles`);
                
                // Calculer les nouveaux totaux par devise
                const existingTotalsByCurrency = calculateTotalsByCurrency([{
                  reservations: existingReservations
                }]);
                const newTotalsByCurrency = calculateTotalsByCurrency([{
                  reservations: uniqueNewReservations
                }]);
                const mergedTotalsByCurrency = mergeTotalsByCurrency(existingTotalsByCurrency, newTotalsByCurrency);

                mergedOwners[existingOwnerIndex] = {
                  ...existingOwner,
                  reservations: [...existingReservations, ...uniqueNewReservations],
                  totals: {
                    ...existingOwner.totals,
                    reservationsCount: existingReservations.length + uniqueNewReservations.length,
                    totalsByCurrency: mergedTotalsByCurrency
                  }
                };
              } else {
                console.log(`✨ Nouveau propriétaire ajouté: ${newOwner.owner.name} (${newOwner.owner.id})`);
                
                // Calculer les totaux par devise pour le nouveau propriétaire
                const newOwnerTotalsByCurrency = calculateTotalsByCurrency([newOwner]);
                
                // Nouveau propriétaire : l'ajouter avec les totaux par devise
                mergedOwners.push({
                  ...newOwner,
                  totals: {
                    ...newOwner.totals,
                    totalsByCurrency: newOwnerTotalsByCurrency
                  }
                });
              }
            });
            
            console.log('🏁 [DEBUG] Données fusionnées final:', {
              totalOwnersCount: mergedOwners.length,
              owners: mergedOwners.map(ownerData => ({
                id: ownerData.owner.id,
                name: ownerData.owner.name,
                reservationsCount: ownerData.reservations?.length || 0
              }))
            });
            
            return mergedOwners;
          });
          setCurrentPage(page);
        } else {
          // Remplacer les données existantes
          setOwnersData(newOwners);
          setCurrentPage(1);
        }

        // Vérifier s'il y a encore des pages
        const pagination = result.data.pagination;
        setHasNextPage(pagination.currentPage < pagination.totalPages);
        setTotalItems(pagination.totalItems || 0);

        console.log('✅ [AdminPaymentsByOwner] Data loaded successfully:', {
          ownersCount: newOwners.length,
          totalItems: pagination?.totalItems,
          hasNextPage: pagination.currentPage < pagination.totalPages,
          isLoadMore,
          mergedData: isLoadMore ? 'Données fusionnées' : 'Données initiales'
        });
      } else {
        setError(result.message || 'Erreur lors du chargement des données');
        if (!isLoadMore) {
          setOwnersData([]);
        }
      }
    } catch (error) {
      console.error('❌ [AdminPaymentsByOwner] Error loading data:', error);
      setError('Erreur de connexion - Veuillez réessayer');
      if (!isLoadMore) {
        setOwnersData([]);
      }
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  /**
   * Handler pour charger plus de données
   */
  const handleLoadMore = () => {
    if (!loadingMore && hasNextPage) {
      fetchOwnerReservations(true);
    }
  };

  /**
   * Charge les données au montage et lors des changements de filtres
   */
  useEffect(() => {
    console.log('🔄 [AdminPaymentsByOwner] useEffect triggered - Filters changed:', filters);
    fetchOwnerReservations(false);
  }, [filters]);

  /**
   * ✅ Charge les devises disponibles au montage du composant
   */
  useEffect(() => {
    const loadAvailableCurrencies = async () => {
      try {
        console.log('🔄 [AdminPaymentsByOwner] Loading available currencies...');
        const result = await adminFinancialService.getAvailableCurrencies();
        
        if (result.success) {
          setAvailableCurrencies(result.data);
          console.log('✅ [AdminPaymentsByOwner] Available currencies loaded:', result.data);
        } else {
          console.error('❌ [AdminPaymentsByOwner] Failed to load available currencies');
          setAvailableCurrencies([]); // Fallback vers liste vide
        }
      } catch (error) {
        console.error('❌ [AdminPaymentsByOwner] Error loading available currencies:', error);
        setAvailableCurrencies([]); // Fallback vers liste vide
      }
    };

    loadAvailableCurrencies();
  }, []); // Une seule fois au montage

  /**
   * Gère les changements de filtres
   */
  const handleFilterChange = (field, value) => {
    console.log(`🔄 [AdminPaymentsByOwner] Filter change: ${field} = ${value}`);
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [field]: value
      };
      console.log('🔄 [AdminPaymentsByOwner] New filters state:', newFilters);
      return newFilters;
    });
    // Reset les données pour recommencer depuis le début
    setOwnersData([]);
    setCurrentPage(1);
    setHasNextPage(true);
    setTotalItems(0);
  };

  /**
   * Applique les filtres de date
   */
  const applyDateFilters = () => {
    console.log('🔄 [AdminPaymentsByOwner] Applying date filters:', filters);
    // Force le rechargement des données avec les nouveaux filtres
    setOwnersData([]);
    setCurrentPage(1);
    setHasNextPage(true);
    setTotalItems(0);
    setShowFilters(false);
    // Le useEffect se déclenchera automatiquement pour recharger les données
  };

  /**
   * Remet à zéro les filtres
   */
  const resetFilters = () => {
    setFilters({
      dateStart: '',
      dateEnd: '',
      ownerId: '',
      reservationStatus: '',
      paymentMethod: '',
      currency: '', // ✅ Nouveau filtre par devise
      ownerEmail: '',
      limit: 5 // Limite optimisée
    });
    setOwnersData([]);
    setCurrentPage(1);
    setHasNextPage(true);
    setTotalItems(0);
  };

  /**
   * Gère l'expansion/contraction d'un propriétaire
   * Évite le scroll automatique lors de l'ouverture
   */
  const handleOwnerToggle = (ownerId, event) => {
    // Empêcher le comportement par défaut qui peut causer un scroll
    if (event) {
      event.preventDefault();
    }
    
    setExpandedOwner(expandedOwner === ownerId ? null : ownerId);
    setExpandedReservation(null); // Fermer toute réservation ouverte
  };

  /**
   * Gère l'expansion/contraction d'une réservation
   */
  const handleReservationToggle = (reservationId) => {
    setExpandedReservation(expandedReservation === reservationId ? null : reservationId);
  };

  /**
   * Gère l'ouverture du modal de détails de réservation
   */
  const handleShowReservationDetails = (reservationId) => {
    setSelectedReservationId(reservationId);
    setIsModalOpen(true);
  };

  /**
   * Gère la fermeture du modal
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReservationId(null);
  };

  /**
   * Gère l'expansion/contraction des détails de devise pour un propriétaire
   */
  const handleCurrencyDetailsToggle = (ownerId, currency) => {
    const key = `${ownerId}-${currency}`;
    setExpandedCurrencyDetails(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  /**
   * Gère l'expansion/contraction de la liste des devises pour un propriétaire
   */
  const handleCurrencyListToggle = (ownerId) => {
    setExpandedCurrencyList(prev => ({
      ...prev,
      [ownerId]: !prev[ownerId]
    }));
  };

  /**
   * Calcule les totaux détaillés par méthode de paiement et par devise
   * @param {Array} owners - Liste des propriétaires avec leurs réservations
   * @returns {Object} Totaux détaillés groupés par méthode de paiement puis par devise
   */
  const calculateTotalsByPaymentMethodAndCurrency = (owners) => {
    const totalsByMethod = {};

    console.log('🧮 [DEBUG] calculateTotalsByPaymentMethodAndCurrency - Input:', owners.length, 'owners');

    owners.forEach(owner => {
      console.log(`📊 [DEBUG] Processing owner: ${owner.owner?.name}, reservations: ${owner.reservations?.length}`);
      
      owner.reservations.forEach((reservation, idx) => {
        const currency = reservation.currency || 'EUR';
        console.log(`💱 [DEBUG] Reservation ${idx}: currency=${currency}, payment_details=`, reservation.payment_details);

        if (reservation.payment_details) {
          console.log(`📋 [DEBUG] Full payment_details structure:`, JSON.stringify(reservation.payment_details, null, 2));
          
          // Traiter chaque type de paiement
          const types = ['rental', 'commission', 'tax', 'penalty'];
          
          types.forEach(type => {
            if (reservation.payment_details[type] && reservation.payment_details[type].payments) {
              reservation.payment_details[type].payments.forEach(payment => {
                // Debug: examiner la structure du paiement
                console.log(`🔍 [DEBUG] Payment structure for ${type}:`, payment);
                console.log(`🔍 [DEBUG] Available payment keys:`, Object.keys(payment));
                
                // Essayer différents champs possibles pour la méthode de paiement
                const method = payment.payment_method || 
                             payment.method || 
                             payment.paymentMethod ||
                             payment.gateway ||
                             payment.processor ||
                             'unknown';
                
                console.log(`💳 [DEBUG] Payment method resolved: ${method} (from ${type})`);
                
                // Initialiser la structure si nécessaire
                if (!totalsByMethod[method]) {
                  totalsByMethod[method] = {};
                }
                if (!totalsByMethod[method][currency]) {
                  totalsByMethod[method][currency] = {
                    currency,
                    rental: 0,
                    commission: 0,
                    tax: 0,
                    penalty: 0,
                    deposit: 0,
                    grandTotal: 0
                  };
                }

                // Ajouter le montant
                const amount = parseFloat(payment.amount || 0);
                totalsByMethod[method][currency][type] += amount;
                totalsByMethod[method][currency].grandTotal += amount;
              });
            }
          });

          // Traiter les dépôts séparément
          if (reservation.payment_details.deposits && reservation.payment_details.deposits.length > 0) {
            reservation.payment_details.deposits.forEach(deposit => {
              // Debug: examiner la structure du dépôt
              console.log(`🔍 [DEBUG] Deposit structure:`, deposit);
              console.log(`🔍 [DEBUG] Available deposit keys:`, Object.keys(deposit));
              
              const method = deposit.payment_method || 
                           deposit.method || 
                           deposit.paymentMethod ||
                           deposit.gateway ||
                           deposit.processor ||
                           'deposit_capture';
              
              console.log(`💰 [DEBUG] Deposit method resolved: ${method}`);
              
              if (!totalsByMethod[method]) {
                totalsByMethod[method] = {};
              }
              if (!totalsByMethod[method][currency]) {
                totalsByMethod[method][currency] = {
                  currency,
                  rental: 0,
                  commission: 0,
                  tax: 0,
                  penalty: 0,
                  deposit: 0,
                  grandTotal: 0
                };
              }

              const amount = parseFloat(deposit.amount || 0);
              totalsByMethod[method][currency].deposit += amount;
              totalsByMethod[method][currency].grandTotal += amount;
            });
          }
        } else {
          // Fallback: essayer de récupérer depuis les paiements principaux de la réservation
          console.log(`⚠️ [DEBUG] No payment_details, trying main payments for reservation ${reservation.id}`);
          console.log(`📋 [DEBUG] Main reservation structure keys:`, Object.keys(reservation));
          
          // Si la réservation a des paiements directs, les traiter
          if (reservation.payments && reservation.payments.length > 0) {
            reservation.payments.forEach(payment => {
              console.log(`🔍 [DEBUG] Main payment structure:`, payment);
              
              const method = payment.payment_method || 
                           payment.method || 
                           payment.paymentMethod ||
                           'unknown_main';
              
              console.log(`💳 [DEBUG] Main payment method: ${method}`);
              
              if (!totalsByMethod[method]) {
                totalsByMethod[method] = {};
              }
              if (!totalsByMethod[method][currency]) {
                totalsByMethod[method][currency] = {
                  currency,
                  rental: 0,
                  commission: 0,
                  tax: 0,
                  penalty: 0,
                  deposit: 0,
                  grandTotal: 0
                };
              }

              const amount = parseFloat(payment.amount || 0);
              // Utiliser 'rental' comme type par défaut pour les paiements principaux
              totalsByMethod[method][currency].rental += amount;
              totalsByMethod[method][currency].grandTotal += amount;
            });
          }
        }
      });
    });

    console.log('🏁 [DEBUG] calculateTotalsByPaymentMethodAndCurrency - Result:', totalsByMethod);
    return totalsByMethod;
  };
  const calculateDetailedTotalsByCurrency = (owners) => {
    const totalsByCurrency = {};

    console.log('🧮 [DEBUG] calculateDetailedTotalsByCurrency - Input:', owners.length, 'owners');

    owners.forEach(owner => {
      console.log(`📊 [DEBUG] Processing owner: ${owner.owner?.name}, reservations: ${owner.reservations?.length}`);
      
      owner.reservations.forEach((reservation, idx) => {
        const currency = reservation.currency || 'EUR';
        console.log(`💱 [DEBUG] Reservation ${idx}: currency=${currency}, payment_details=`, reservation.payment_details);

        // Initialiser la structure pour cette devise si nécessaire
        if (!totalsByCurrency[currency]) {
          totalsByCurrency[currency] = {
            currency,
            rental: { total: 0, count: 0 },
            commission: { total: 0, count: 0 },
            tax: { total: 0, count: 0 },
            penalty: { total: 0, count: 0 },
            deposit: { total: 0, count: 0 },
            grandTotal: 0
          };
        }

        const currencyTotals = totalsByCurrency[currency];

        // Traiter les détails de paiement si disponibles
        if (reservation.payment_details) {
          console.log(`✅ [DEBUG] Payment details found for reservation ${reservation.id}`);
          const details = reservation.payment_details;

          // Rental
          if (details.rental) {
            currencyTotals.rental.total += details.rental.total || 0;
            currencyTotals.rental.count += details.rental.payments?.length || 0;
          }

          // Commission
          if (details.commission) {
            currencyTotals.commission.total += details.commission.total || 0;
            currencyTotals.commission.count += details.commission.payments?.length || 0;
          }

          // Tax
          if (details.tax) {
            currencyTotals.tax.total += details.tax.total || 0;
            currencyTotals.tax.count += details.tax.payments?.length || 0;
          }

          // Penalty
          if (details.penalty) {
            currencyTotals.penalty.total += details.penalty.total || 0;
            currencyTotals.penalty.count += details.penalty.payments?.length || 0;
          }

          // Deposits
          if (details.deposits?.length > 0) {
            details.deposits.forEach(deposit => {
              currencyTotals.deposit.total += parseFloat(deposit.amount || 0);
              currencyTotals.deposit.count += 1;
            });
          }
        } else {
          console.log(`⚠️ [DEBUG] No payment details for reservation ${reservation.id}, using fallback total_amount: ${reservation.total_amount}`);
          // Fallback: utiliser le montant total de la réservation comme rental
          const amount = parseFloat(reservation.total_amount || 0);
          currencyTotals.rental.total += amount;
          currencyTotals.rental.count += 1;
        }

        // Calculer le grand total pour cette devise
        currencyTotals.grandTotal = 
          currencyTotals.rental.total + 
          currencyTotals.commission.total + 
          currencyTotals.tax.total + 
          currencyTotals.penalty.total + 
          currencyTotals.deposit.total;
      });
    });

    console.log('🏁 [DEBUG] calculateDetailedTotalsByCurrency - Result:', totalsByCurrency);
    return totalsByCurrency;
  };

  /**
   * Calcule les totaux par devise pour éviter l'addition de devises différentes
   * @param {Array} owners - Liste des propriétaires avec leurs données
   * @returns {Object} Totaux groupés par devise
   */
  const calculateTotalsByCurrency = (owners) => {
    const totalsByCurrency = {};

    owners.forEach(owner => {
      // Parcourir toutes les réservations du propriétaire
      owner.reservations.forEach(reservation => {
        const currency = reservation.currency || 'EUR';
        const amount = parseFloat(reservation.total_amount || 0);

        if (!totalsByCurrency[currency]) {
          totalsByCurrency[currency] = {
            total: 0,
            count: 0,
            currency: currency
          };
        }

        totalsByCurrency[currency].total += amount;
        totalsByCurrency[currency].count += 1;
      });
    });

    return totalsByCurrency;
  };

  /**
   * Fusionne les totaux de deux propriétaires en tenant compte des devises
   * @param {Object} existingTotals - Totaux existants
   * @param {Object} newTotals - Nouveaux totaux à fusionner
   * @returns {Object} Totaux fusionnés par devise
   */
  const mergeTotalsByCurrency = (existingTotals, newTotals) => {
    const merged = { ...existingTotals };

    Object.keys(newTotals).forEach(currency => {
      if (merged[currency]) {
        merged[currency].total += newTotals[currency].total;
        merged[currency].count += newTotals[currency].count;
      } else {
        merged[currency] = { ...newTotals[currency] };
      }
    });

    return merged;
  };

  /**
   * Composant pour afficher les totaux par méthode de paiement et par devise
   * @param {Object} totalsByMethod - Totaux groupés par méthode de paiement puis par devise
   * @returns {JSX.Element}
   */
  const renderTotalsByPaymentMethod = (totalsByMethod) => {
    if (!totalsByMethod || Object.keys(totalsByMethod).length === 0) {
      return <Typography variant="body2" color="textSecondary">Aucun paiement trouvé</Typography>;
    }

    const methods = Object.keys(totalsByMethod);

    return (
      <Box>
        {methods.map(method => {
          const methodData = totalsByMethod[method];
          const currencies = Object.keys(methodData);
          const methodLabel = PAYMENT_METHOD_LABELS[method] || method;

          return (
            <Card key={method} sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ pb: '16px !important' }}>
                {/* En-tête de la méthode de paiement */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Chip 
                    label={methodLabel}
                    color="primary"
                    variant="filled"
                    sx={{ mr: 2, fontWeight: 'bold' }}
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {currencies.length} devise{currencies.length > 1 ? 's' : ''}
                  </Typography>
                </Box>

                {/* Devises pour cette méthode */}
                <Grid container spacing={2}>
                  {currencies.map(currency => {
                    const data = methodData[currency];

                    return (
                      <Grid item xs={12} md={6} key={currency}>
                        <Paper sx={{ p: 2, border: '1px solid #f0f0f0' }}>
                          {/* En-tête devise */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                              {currency}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                              {formatCurrency(data.grandTotal, currency)}
                            </Typography>
                          </Box>

                          {/* Détails par type */}
                          <Grid container spacing={1}>
                            {data.rental > 0 && (
                              <Grid item xs={6}>
                                <Chip 
                                  label={`Location: ${formatCurrency(data.rental, currency)}`}
                                  size="small" 
                                  color="success" 
                                  variant="outlined"
                                  sx={{ width: '100%', fontSize: '0.7rem' }}
                                />
                              </Grid>
                            )}

                            {data.commission > 0 && (
                              <Grid item xs={6}>
                                <Chip 
                                  label={`Commission: ${formatCurrency(data.commission, currency)}`}
                                  size="small" 
                                  color="info" 
                                  variant="outlined"
                                  sx={{ width: '100%', fontSize: '0.7rem' }}
                                />
                              </Grid>
                            )}

                            {data.tax > 0 && (
                              <Grid item xs={6}>
                                <Chip 
                                  label={`Taxes: ${formatCurrency(data.tax, currency)}`}
                                  size="small" 
                                  color="warning" 
                                  variant="outlined"
                                  sx={{ width: '100%', fontSize: '0.7rem' }}
                                />
                              </Grid>
                            )}

                            {data.deposit > 0 && (
                              <Grid item xs={6}>
                                <Chip 
                                  label={`Dépôts: ${formatCurrency(data.deposit, currency)}`}
                                  size="small" 
                                  color="secondary" 
                                  variant="outlined"
                                  sx={{ width: '100%', fontSize: '0.7rem' }}
                                />
                              </Grid>
                            )}

                            {data.penalty > 0 && (
                              <Grid item xs={6}>
                                <Chip 
                                  label={`Pénalités: ${formatCurrency(data.penalty, currency)}`}
                                  size="small" 
                                  color="error" 
                                  variant="outlined"
                                  sx={{ width: '100%', fontSize: '0.7rem' }}
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  /**
   * Calcule les statistiques des flags de méthode de paiement
   * @param {Array} owners - Liste des propriétaires avec leurs réservations
   * @returns {Object} Statistiques des flags de paiement
   */
  const calculatePaymentFlagStats = (owners) => {
    const stats = {
      cash: { count: 0, amount: 0 },
      online: { count: 0, amount: 0 },
      mixed: { count: 0, amount: 0 },
      other: { count: 0, amount: 0 },
      unknown: { count: 0, amount: 0 }
    };

    owners.forEach(owner => {
      owner.reservations.forEach(reservation => {
        const flagType = reservation.payment_flag?.type || 'unknown';
        const amount = parseFloat(reservation.total_amount || 0);
        
        if (stats[flagType]) {
          stats[flagType].count++;
          stats[flagType].amount += amount;
        }
      });
    });

    return stats;
  };

  /**
   * Render des statistiques des flags de paiement
   * @param {Object} stats - Statistiques calculées
   * @returns {JSX.Element}
   */
  const renderPaymentFlagStats = (stats) => {
    const flagConfig = {
      cash: { icon: '💵', label: 'Cash', color: '#4caf50' },
      online: { icon: '💳', label: 'En ligne', color: '#2196f3' },
      mixed: { icon: '🔄', label: 'Mixte', color: '#ff9800' },
      other: { icon: '🏦', label: 'Autre', color: '#9c27b0' },
      unknown: { icon: '❓', label: 'Inconnu', color: '#9e9e9e' }
    };

    const totalCount = Object.values(stats).reduce((sum, data) => sum + data.count, 0);

    return (
      <Box>
        {Object.entries(stats).map(([type, data]) => {
          if (data.count === 0) return null;
          
          const config = flagConfig[type];
          const percentage = totalCount > 0 ? ((data.count / totalCount) * 100).toFixed(0) : 0;
          
          return (
            <Box key={type} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: '6px', fontSize: '14px' }}>{config.icon}</span>
                  <Typography variant="body2" sx={{ color: config.color, fontWeight: 'bold', fontSize: '0.85rem' }}>
                    {config.label}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: config.color }}>
                  {data.count}
                </Typography>
              </Box>
              <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 4 }}>
                <Box
                  sx={{
                    width: `${percentage}%`,
                    bgcolor: config.color,
                    borderRadius: 1,
                    height: '100%',
                    transition: 'width 0.3s ease-in-out'
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderDetailedTotalsByCurrency = (totalsByCurrency) => {
    if (!totalsByCurrency || Object.keys(totalsByCurrency).length === 0) {
      return <Typography variant="body2" color="textSecondary">Aucun montant</Typography>;
    }

    const currencies = Object.keys(totalsByCurrency);

    return (
      <Box>
        {currencies.map(currency => {
          const data = totalsByCurrency[currency];
          const hasDetails = data.rental.total > 0 || data.commission.total > 0 || 
                           data.tax.total > 0 || data.deposit.total > 0;

          return (
            <Card key={currency} sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
              <CardContent sx={{ pb: '16px !important' }}>
                {/* En-tête avec total principal */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                    {currency}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatCurrency(data.grandTotal, currency)}
                  </Typography>
                </Box>

                {/* Détails par type si disponibles */}
                {hasDetails && (
                  <Grid container spacing={1}>
                    {data.rental.total > 0 && (
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                          <Typography variant="caption" display="block">Location</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(data.rental.total, currency)}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    {data.commission.total > 0 && (
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
                          <Typography variant="caption" display="block">Commission</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(data.commission.total, currency)}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    {data.tax.total > 0 && (
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                          <Typography variant="caption" display="block">Taxes</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(data.tax.total, currency)}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    {data.deposit.total > 0 && (
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
                          <Typography variant="caption" display="block">Dépôts</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(data.deposit.total, currency)}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}

                    {data.penalty.total > 0 && (
                      <Grid item xs={6} sm={3}>
                        <Paper sx={{ p: 1, textAlign: 'center', bgcolor: 'error.light', color: 'error.contrastText' }}>
                          <Typography variant="caption" display="block">Pénalités</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(data.penalty.total, currency)}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  };

  /**
   * Composant pour afficher les totaux par devise
   * @param {Object} totalsByCurrency - Totaux groupés par devise
   * @returns {JSX.Element}
   */
  const renderTotalsByCurrency = (totalsByCurrency) => {
    if (!totalsByCurrency || Object.keys(totalsByCurrency).length === 0) {
      return <Typography variant="body2" color="textSecondary">Aucun montant</Typography>;
    }

    const currencies = Object.keys(totalsByCurrency);
    
    if (currencies.length === 1) {
      // Une seule devise
      const currency = currencies[0];
      const data = totalsByCurrency[currency];
      return (
        <Typography variant="h6" component="div">
          {formatCurrency(data.total, currency)}
        </Typography>
      );
    }

    // Plusieurs devises
    return (
      <Box>
        {currencies.map(currency => {
          const data = totalsByCurrency[currency];
          return (
            <Typography key={currency} variant="body1" component="div">
              {formatCurrency(data.total, currency)}
            </Typography>
          );
        })}
      </Box>
    );
  };

  /**
   * Formate un montant en devise
   */
  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  /**
   * Render du panneau de filtres
   */
  const renderFilters = () => (
    <Collapse in={showFilters}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filtres de recherche
          </Typography>
          <Grid container spacing={2}>
            {/* Première ligne - Filtres principaux */}
            {/* Filtres de date */}
            <Grid item xs={12} sm={6} lg={2}>
              <TextField
                label="Date début"
                type="date"
                value={filters.dateStart}
                onChange={(e) => handleFilterChange('dateStart', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={2}>
              <TextField
                label="Date fin"
                type="date"
                value={filters.dateEnd}
                onChange={(e) => handleFilterChange('dateEnd', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
              />
            </Grid>
            
            {/* Filtres par statut */}
            <Grid item xs={12} sm={6} lg={2}>
              <TextField
                select
                label="Statut réservation"
                value={filters.reservationStatus}
                onChange={(e) => handleFilterChange('reservationStatus', e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="">Tous les statuts</MenuItem>
                {Object.keys(RESERVATION_STATUS).map(key => (
                  <MenuItem key={key} value={RESERVATION_STATUS[key]}>
                    {RESERVATION_STATUS_LABELS[RESERVATION_STATUS[key]]}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Filtres par méthode de paiement */}
            <Grid item xs={12} sm={6} lg={2}>
              <TextField
                select
                label="Méthode paiement"
                value={filters.paymentMethod}
                onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="">Toutes les méthodes</MenuItem>
                {Object.keys(PAYMENT_METHOD).map(key => (
                  <MenuItem key={key} value={PAYMENT_METHOD[key]}>
                    {PAYMENT_METHOD_LABELS[PAYMENT_METHOD[key]]}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* ✅ Filtre par devise dynamique */}
            <Grid item xs={12} sm={6} lg={2}>
              <TextField
                select
                label="Devise"
                value={filters.currency}
                onChange={(e) => handleFilterChange('currency', e.target.value)}
                fullWidth
                size="small"
              >
                <MenuItem value="">Toutes les devises</MenuItem>
                {availableCurrencies.map(currency => (
                  <MenuItem key={currency.code} value={currency.code}>
                    {currency.label} ({currency.count})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Email propriétaire */}
            <Grid item xs={12} sm={6} lg={2}>
              <TextField
                label="Email propriétaire"
                value={filters.ownerEmail}
                onChange={(e) => handleFilterChange('ownerEmail', e.target.value)}
                placeholder="recherche partielle..."
                fullWidth
                size="small"
              />
            </Grid>

            {/* ID Propriétaire */}
            <Grid item xs={12} sm={6} lg={2}>
              <TextField
                label="ID Propriétaire"
                value={filters.ownerId}
                onChange={(e) => handleFilterChange('ownerId', e.target.value)}
                placeholder="Optionnel"
                fullWidth
                size="small"
              />
            </Grid>

            {/* Boutons d'action */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mt: 2 }}>
                <Button 
                  variant="contained" 
                  onClick={applyDateFilters}
                  disabled={loading}
                  sx={{ minWidth: 120 }}
                >
                  Appliquer
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={resetFilters}
                  disabled={loading}
                  sx={{ minWidth: 120 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Collapse>
  );

  /**
   * Render des détails d'un type de paiement
   */
  const renderPaymentType = (label, paymentType, color) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color={color} gutterBottom>
        {label}
      </Typography>
      <Paper sx={{ p: 1, backgroundColor: 'grey.50' }}>
        <Typography variant="body2">
          <strong>Total: {formatCurrency(paymentType.total)}</strong>
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Transactions: {paymentType.payments?.length || 0}
        </Typography>
        {paymentType.payments?.slice(0, 3).map((payment, index) => (
          <Typography key={index} variant="caption" display="block">
            • {formatCurrency(payment.amount)} - {dayjs(payment.transaction_date).format('DD/MM/YYYY')}
          </Typography>
        ))}
        {paymentType.payments?.length > 3 && (
          <Typography variant="caption" color="textSecondary">
            ... et {paymentType.payments.length - 3} autres
          </Typography>
        )}
      </Paper>
    </Box>
  );

  /**
   * Render des détails de paiement d'une réservation
   */
  const renderPaymentDetails = (paymentDetails) => (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12} sm={6}>
        {renderPaymentType('Location', paymentDetails.rental, 'primary.main')}
      </Grid>
      <Grid item xs={12} sm={6}>
        {renderPaymentType('Commission', paymentDetails.commission, 'warning.main')}
      </Grid>
      <Grid item xs={12} sm={6}>
        {renderPaymentType('Taxes', paymentDetails.tax, 'error.main')}
      </Grid>
      <Grid item xs={12} sm={6}>
        {renderPaymentType('Pénalités', paymentDetails.penalty, 'error.dark')}
      </Grid>
      <Grid item xs={12}>
        <Divider sx={{ my: 1 }} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="success.main">
            Total général: {formatCurrency(paymentDetails.summary?.grandTotal || 0)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            (Paiements: {formatCurrency(paymentDetails.summary?.totalPayments || 0)} + 
            Dépôts: {formatCurrency(paymentDetails.summary?.totalDeposits || 0)})
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );

  /**
   * Render d'une réservation
   */
  const renderReservation = (reservation) => (
    <Card key={reservation.id} sx={{ mb: 1, ml: 2 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer'
          }}
          onClick={() => handleReservationToggle(reservation.id)}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1">
                {reservation.listing.title}
              </Typography>
              {/* ✅ Affichage du flag de méthode de paiement */}
              {reservation.payment_flag && (
                <Chip
                  label={`${reservation.payment_flag.icon} ${reservation.payment_flag.label}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    backgroundColor: reservation.payment_flag.color + '15', // Transparence plus légère
                    borderColor: reservation.payment_flag.color,
                    color: reservation.payment_flag.color,
                    fontWeight: '500',
                    fontSize: '0.7rem',
                    height: '22px',
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                />
              )}
            </Box>
            <Typography variant="body2" color="textSecondary">
              {dayjs(reservation.start_date).format('DD/MM/YYYY')} - {dayjs(reservation.end_date).format('DD/MM/YYYY')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Locataire: {reservation.tenant.name}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Chip 
              label={reservation.status} 
              size="small" 
              color={reservation.status === 'confirmed' ? 'success' : 'default'}
              sx={{ mb: 1 }}
            />
            <Typography variant="h6" color="primary">
              {formatCurrency(reservation.total_amount, reservation.currency)}
            </Typography>
            <IconButton size="small">
              {expandedReservation === reservation.id ? <ExpandLessIcon /> : <ExpandMoreDownIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expandedReservation === reservation.id}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Détails des paiements
          </Typography>
          {reservation.payment_details ? 
            renderPaymentDetails(reservation.payment_details) : 
            <Typography color="textSecondary">Aucun détail de paiement disponible</Typography>
          }
          
          {/* ✅ Bouton pour ouvrir le modal de détails */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => handleShowReservationDetails(reservation.id)}
            >
              Voir détails complets
            </Button>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );

  /**
   * Render d'un propriétaire avec ses réservations
   */
  const renderOwner = (ownerData) => (
    <Accordion 
      key={ownerData.owner.id}
      expanded={expandedOwner === ownerData.owner.id}
      onChange={(event, isExpanded) => handleOwnerToggle(ownerData.owner.id, event)}
      TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
      sx={{ '& .MuiCollapse-root': { overflow: 'visible' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">
              {ownerData.owner.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {ownerData.owner.email}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', mr: 2 }}>
            <Box color="success.main">
              {(() => {
                // Debug: vérifier les données du propriétaire
                console.log(`🔍 [DEBUG] Owner ${ownerData.owner.name}:`, {
                  reservationsCount: ownerData.reservations?.length,
                  totalsStructure: ownerData.totals,
                  sampleReservation: ownerData.reservations?.[0],
                  samplePaymentDetails: ownerData.reservations?.[0]?.payment_details
                });

                // Calculer les totaux détaillés pour ce propriétaire
                const ownerDetailedTotals = calculateDetailedTotalsByCurrency([ownerData]);
                console.log(`💰 [DEBUG] Totaux détaillés pour ${ownerData.owner.name}:`, ownerDetailedTotals);
                
                const currencies = Object.keys(ownerDetailedTotals);
                
                // Affichage élégant avec liste déroulante master pour les devises
                return (
                  <Box sx={{ minWidth: 250 }}>
                    {/* Header élégant de la liste des devises */}
                    <Card 
                      sx={{ 
                        mb: 1, 
                        border: '1px solid #e0e0e0', 
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 2 },
                        background: 'linear-gradient(135deg, #f5f5f5 0%, #e8f5e8 100%)'
                      }}
                      onClick={(event) => {
                        event.stopPropagation(); // Empêcher la propagation vers l'accordion parent
                        handleCurrencyListToggle(ownerData.owner.id);
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                              Devises ({currencies.length})
                            </Typography>
                            {/* Aperçu compact des totaux */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {currencies.map(currency => {
                                const data = ownerDetailedTotals[currency];
                                return (
                                  <Chip
                                    key={currency}
                                    label={`${currency}: ${formatCurrency(data.grandTotal, currency)}`}
                                    size="small"
                                    variant="filled"
                                    color="success"
                                    sx={{ fontSize: '0.7rem', height: 20 }}
                                  />
                                );
                              })}
                            </Box>
                          </Box>
                          <IconButton size="small" sx={{ color: 'success.main' }}>
                            {expandedCurrencyList[ownerData.owner.id] ? <ExpandLessIcon /> : <ExpandMoreDownIcon />}
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Liste déroulante des devises élégantes */}
                    <Collapse in={expandedCurrencyList[ownerData.owner.id]}>
                      <Box sx={{ pl: 1, borderLeft: '3px solid #e8f5e8' }}>
                        {currencies.map(currency => {
                          const data = ownerDetailedTotals[currency];
                          const detailsKey = `${ownerData.owner.id}-${currency}`;
                          const isExpanded = expandedCurrencyDetails[detailsKey] || false;
                          
                          return (
                            <Card key={currency} sx={{ mb: 1, border: '1px solid #e0e0e0', ml: 1 }}>
                              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                {/* En-tête avec total principal cliquable */}
                                <Box 
                                  sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                                    p: 0.5,
                                    borderRadius: 1
                                  }}
                                  onClick={(event) => {
                                    event.stopPropagation(); // Empêcher la propagation vers l'accordion parent
                                    handleCurrencyDetailsToggle(ownerData.owner.id, currency);
                                  }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                    {currency}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                      {formatCurrency(data.grandTotal, currency)}
                                    </Typography>
                                    <IconButton size="small">
                                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreDownIcon />}
                                    </IconButton>
                                  </Box>
                                </Box>

                                {/* Détails déroulants de chaque devise */}
                                <Collapse in={isExpanded}>
                                  <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                                    <Grid container spacing={0.5}>
                                      {/* Location */}
                                      {data.rental.total > 0 && (
                                        <Grid item xs={6}>
                                          <Chip 
                                            label={`Location: ${formatCurrency(data.rental.total, currency)}`}
                                            size="small" 
                                            color="success" 
                                            variant="outlined"
                                            sx={{ width: '100%', fontSize: '0.75rem' }}
                                          />
                                        </Grid>
                                      )}
                                      
                                      {/* Commission */}
                                      {data.commission.total > 0 && (
                                        <Grid item xs={6}>
                                          <Chip 
                                            label={`Commission: ${formatCurrency(data.commission.total, currency)}`}
                                            size="small" 
                                            color="info" 
                                            variant="outlined"
                                            sx={{ width: '100%', fontSize: '0.75rem' }}
                                          />
                                        </Grid>
                                      )}
                                      
                                      {/* Taxes */}
                                      {data.tax.total > 0 && (
                                        <Grid item xs={6}>
                                          <Chip 
                                            label={`Taxes: ${formatCurrency(data.tax.total, currency)}`}
                                            size="small" 
                                            color="warning" 
                                            variant="outlined"
                                            sx={{ width: '100%', fontSize: '0.75rem' }}
                                          />
                                        </Grid>
                                      )}
                                      
                                      {/* Dépôts */}
                                      {data.deposit.total > 0 && (
                                        <Grid item xs={6}>
                                          <Chip 
                                            label={`Dépôts: ${formatCurrency(data.deposit.total, currency)}`}
                                            size="small" 
                                            color="secondary" 
                                            variant="outlined"
                                            sx={{ width: '100%', fontSize: '0.75rem' }}
                                          />
                                        </Grid>
                                      )}
                                      
                                      {/* Pénalités */}
                                      {data.penalty.total > 0 && (
                                        <Grid item xs={6}>
                                          <Chip 
                                            label={`Pénalités: ${formatCurrency(data.penalty.total, currency)}`}
                                            size="small" 
                                            color="error" 
                                            variant="outlined"
                                            sx={{ width: '100%', fontSize: '0.75rem' }}
                                          />
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Box>
                                </Collapse>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })()}
            </Box>
            <Typography variant="body2" color="textSecondary">
              {ownerData.reservations?.length || 0} réservation(s) chargées
              {ownerData.totals.reservationsCount > (ownerData.reservations?.length || 0) && 
                ` sur ${ownerData.totals.reservationsCount}`
              }
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="h6" gutterBottom>
          Réservations ({ownerData.reservations.length})
        </Typography>
        {ownerData.reservations.map(renderReservation)}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Paiements par Propriétaire
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Vue administrative des transactions financières groupées par propriétaire
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setShowFilters(!showFilters)}
          disabled={loading}
        >
          Filtres
        </Button>
      </Box>

      {/* Filtres */}
      {renderFilters()}

      {/* Statistiques rapides */}
      {!loading && ownersData.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {/* 🔥 Métriques principales - Ligne 1 */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <BusinessIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {ownersData.length}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    Propriétaires
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <WalletIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {ownersData.reduce((sum, owner) => sum + owner.totals.reservationsCount, 0)}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    Réservations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <WalletIcon sx={{ fontSize: 48, color: 'info.main', mb: 1 }} />
                  <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {totalItems}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    Total Items
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent sx={{ py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <WalletIcon sx={{ fontSize: 32, color: 'secondary.main', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Types Paiement
                    </Typography>
                  </Box>
                  {(() => {
                    const paymentFlagStats = calculatePaymentFlagStats(ownersData);
                    return renderPaymentFlagStats(paymentFlagStats);
                  })()}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* 🔥 Analyses détaillées - Ligne 2 */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent sx={{ py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <WalletIcon sx={{ fontSize: 32, color: 'warning.main', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Analyse par Devise
                    </Typography>
                  </Box>
                  {(() => {
                    const globalDetailedTotals = calculateDetailedTotalsByCurrency(ownersData);
                    return renderDetailedTotalsByCurrency(globalDetailedTotals);
                  })()}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={3} sx={{ height: '100%' }}>
                <CardContent sx={{ py: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <WalletIcon sx={{ fontSize: 32, color: 'info.main', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      Analyse par Méthode
                    </Typography>
                  </Box>
                  {(() => {
                    const totalsByMethod = calculateTotalsByPaymentMethodAndCurrency(ownersData);
                    return renderTotalsByPaymentMethod(totalsByMethod);
                  })()}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Gestion des états de chargement et d'erreur */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Informations de pagination en haut */}
      {!loading && !error && ownersData.length > 0 && (
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            📊 Affichage de <strong>{ownersData.length}</strong> propriétaires sur <strong>{totalItems}</strong> au total
            {hasNextPage && (
              <span style={{ marginLeft: '8px' }}>
                • <strong>{totalItems - ownersData.length}</strong> restants à charger
              </span>
            )}
          </Typography>
        </Box>
      )}

      {/* Liste des propriétaires avec bouton Load More */}
      {!loading && !error && ownersData.length > 0 && (
        <Box>
          {ownersData.map(renderOwner)}
        </Box>
      )}

      {/* Bouton Load More */}
      {!loading && !error && ownersData.length > 0 && hasNextPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
          <Button
            variant="contained"
            onClick={handleLoadMore}
            disabled={loadingMore}
            size="large"
            sx={{
              minWidth: 200,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #1CB5E3 90%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 10px 2px rgba(33, 203, 243, .3)',
              },
              '&:disabled': {
                background: '#ccc',
                boxShadow: 'none'
              }
            }}
          >
            {loadingMore ? (
              <>
                <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                Chargement...
              </>
            ) : (
              'Charger plus de données'
            )}
          </Button>
        </Box>
      )}

      {/* Indicateur de chargement pour plus de données (optionnel) */}
      {loadingMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            📡 Récupération des données en cours...
          </Typography>
        </Box>
      )}

      {/* Message de fin de données */}
      {!loading && !loadingMore && !hasNextPage && ownersData.length > 0 && (
        <Box sx={{ textAlign: 'center', mt: 3, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Toutes les données ont été chargées
          </Typography>
        </Box>
      )}

      {/* Message si aucune donnée */}
      {!loading && !error && ownersData.length === 0 && (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Aucune donnée trouvée
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Essayez de modifier vos critères de recherche
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* ✅ Modal de détails de réservation */}
      <ReservationDetailsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        reservationId={selectedReservationId}
      />
    </Box>
  );
};

export default AdminPaymentsByOwner;